import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminStats } from '../../api';
import { AdminStats } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';

export function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      loadStats();
    }
  }, [isAuthenticated, user]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminStats();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const formatCost = (micros: number) => {
    return `$${(micros / 1_000_000).toFixed(4)}`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-dark-200">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  return (
    <AdminLayout
      title="Admin Dashboard"
      breadcrumbs={[{ label: 'Admin' }]}
      actions={
        <div className="text-sm text-dark-300">
          Logged in as: <span className="text-primary-400">{user.email}</span>
        </div>
      }
    >
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Users" 
          value={formatNumber(stats?.totalUsers || 0)} 
          subtext={`+${stats?.usersLast24h || 0} last 24h`}
        />
        <StatCard 
          title="Total Worlds" 
          value={formatNumber(stats?.totalWorlds || 0)} 
          subtext={`${stats?.activeWorldsLast24h || 0} active last 24h`}
        />
        <StatCard 
          title="Total Messages" 
          value={formatNumber(stats?.totalMessages || 0)} 
        />
        <StatCard
          title="Total Tokens"
          value={formatNumber(stats?.totalTokensUsed || 0)}
        />
        <StatCard
          title="COGS"
          value={formatCost(stats?.totalCostMicros || 0)}
        />
        <StatCard
          title="Billed"
          value={formatCost(stats?.totalBilledMicros || 0)}
        />
        <StatCard
          title="Profit"
          value={formatCost((stats?.totalBilledMicros || 0) - (stats?.totalCostMicros || 0))}
        />
      </div>

      {/* Navigation Cards */}
      <h2 className="text-xl font-semibold text-dark-50 mb-4">Admin Tools</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <NavCard 
          to="/admin/users"
          title="Users"
          description="View and manage user accounts, see usage statistics per user"
          icon="👥"
        />
        <NavCard 
          to="/admin/worlds"
          title="Worlds"
          description="Inspect world data, messages, characters, and extracted facts"
          icon="🌍"
        />
        <NavCard 
          to="/admin/invitations"
          title="Invitations"
          description="Create and manage invitation codes for access"
          icon="🎟️"
        />
        <NavCard 
          to="/admin/usage"
          title="Usage Analytics"
          description="View API usage, token consumption, and cost breakdowns"
          icon="📊"
        />
        <NavCard 
          to="/admin/inference"
          title="Inference Logs"
          description="Full history of all LLM requests and responses"
          icon="🤖"
        />
        <NavCard 
          to="/admin/scaffolding"
          title="World Creation Traces"
          description="View scaffolding agent traces from world creation"
          icon="🏗️"
        />
      </div>
    </AdminLayout>
  );
}

function StatCard({ title, value, subtext }: { title: string; value: string; subtext?: string }) {
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
      <h3 className="text-dark-400 text-sm font-medium mb-2">{title}</h3>
      <p className="text-3xl font-bold text-dark-50">{value}</p>
      {subtext && <p className="text-dark-400 text-sm mt-1">{subtext}</p>}
    </div>
  );
}

function NavCard({ to, title, description, icon }: { to: string; title: string; description: string; icon: string }) {
  return (
    <Link 
      to={to}
      className="bg-dark-800 border border-dark-600 rounded-lg p-6 hover:border-primary-500 transition-colors"
    >
      <div className="flex items-start gap-4">
        <span className="text-3xl">{icon}</span>
        <div>
          <h3 className="text-lg font-semibold text-dark-50 mb-1">{title}</h3>
          <p className="text-dark-400 text-sm">{description}</p>
        </div>
      </div>
    </Link>
  );
}
